import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FaArrowLeft,
  FaBroadcastTower,
  FaCheck,
  FaChevronDown,
  FaCrown,
  FaGamepad,
  FaHistory,
  FaLayerGroup,
  FaPaintBrush,
  FaPlus,
  FaQrcode,
  FaTag,
  FaTrophy,
  FaTv,
  FaUsers,
} from "react-icons/fa";
import { getModesToDisplay } from "../api/mode";
import { getPlans } from "../api/payments";
import { useAuth } from "../contexts/AuthContext";
import { PREMIUM_BENEFITS } from "../constants/subscriptionTiers";
import { Badge, Button, Card, Container, Section } from "../ui";
import PublicLayout from "../components/site/PublicLayout";
import CodeEntry from "../components/site/CodeEntry";
import { GameCodeHelp } from "../components/site/CodeEntryModal";
import { StoreBadges } from "../components/site/SiteFooter";
import logo from "../assets/images/IMG_4242.PNG";
import { formatMoney } from "../utils/money";

const STEPS = [
  {
    Icon: FaPlus,
    title: "أنشئ لعبتك",
    text: "من الموقع أو التطبيق: اختر الفئات وسمّ الفريقين.",
  },
  {
    Icon: FaTv,
    title: "اعرضها على الشاشة",
    text: "افتحها على التلفزيون أو اللابتوب. لعبة من التطبيق تعطيك كود من 6 خانات تكتبه هنا.",
  },
  {
    Icon: FaTrophy,
    title: "العبوا وتنافسوا",
    text: "اختاروا الأسئلة من اللوحة، جاوبوا واجمعوا النقاط، والأعلى يفوز.",
  },
];

const FEATURES = [
  {
    Icon: FaLayerGroup,
    title: "فئات بكل المجالات",
    text: "ثقافة، رياضة، ألغاز وأكثر، وفئات جديدة باستمرار.",
  },
  {
    Icon: FaUsers,
    title: "فئات المجتمع",
    text: "العب فئات صنعها اللاعبين، أو اصنع فئتك وشاركها.",
  },
  {
    Icon: FaBroadcastTower,
    title: "البث المباشر",
    text: "خلّ الجمهور يتابع اللعبة ويصوّت من جواله.",
  },
  {
    Icon: FaPaintBrush,
    title: "وضع الرسم",
    text: "أسئلة يرسمها اللاعب من جواله وتظهر على الشاشة مباشرة.",
  },
  {
    Icon: FaQrcode,
    title: "أسئلة بالـ QR",
    text: "بعض الأسئلة تنمسح بالجوال وتظهر للاعب وحده.",
  },
  {
    Icon: FaTv,
    title: "على أي شاشة",
    text: "تلفزيون، لابتوب أو آيباد. يكفيك متصفح.",
  },
];

const FAQ = [
  {
    q: "وش الفرق بين كود اللعبة وكود الخصم؟",
    a: (
      <p>
        <b className="text-white">كود اللعبة</b> من 6 خانات ويفتح لعبة جاهزة على
        الشاشة. <b className="text-white">كود الخصم</b> يعطيك سعر أقل على
        الاشتراك ويُستخدم في صفحة الاشتراك. وإذا كتبت أي كود في «عندك كود؟» نعرف
        نوعه ونوديك للمكان الصحيح.
      </p>
    ),
  },
  { q: "كيف أحصل على كود اللعبة؟", a: <GameCodeHelp /> },
  {
    q: "وين أحط كود الخصم؟",
    a: (
      <p>
        افتح الرابط اللي وصلك، أو ادخل صفحة الاشتراك واضغط «عندك كود خصم؟». تشوف
        السعر بعد الخصم قبل الدفع، وتسجّل الدخول وقت الاشتراك.
      </p>
    ),
  },
  {
    q: "أقدر ألعب بدون اشتراك؟",
    a: (
      <p>
        إي. الحساب المجاني يعطيك عدد محدود من الألعاب والفئات، والاشتراك يفتحها
        بلا حدود مع كل المزايا.
      </p>
    ),
  },
];

const FaqItem = ({ q, children }) => (
  <details className="group rounded-2xl border border-white/10 bg-white/[0.05] open:bg-white/[0.08] transition-colors">
    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 font-bold text-white [&::-webkit-details-marker]:hidden">
      {q}
      <FaChevronDown className="shrink-0 text-sm text-white/60 transition-transform group-open:rotate-180" />
    </summary>
    <div className="px-5 pb-5 text-white/75 leading-relaxed">{children}</div>
  </details>
);

const Home = () => {
  const { isAuthenticated } = useAuth();
  const [oldGame, setOldGame] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const { data: modes } = useQuery({
    queryKey: ["modes"],
    queryFn: () => getModesToDisplay(),
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { data: plansData } = useQuery({ queryKey: ["plans"], queryFn: getPlans });
  const cheapest = (plansData?.plans || [])
    .filter((p) => p.price > 0)
    .sort((a, b) => a.price - b.price)[0];

  useEffect(() => {
    setOldGame(!!localStorage.getItem("game"));
  }, []);

  useEffect(() => {
    // Prevent right-click context menu (kept from the old home page)
    const handleContextMenu = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handleContextMenu);
    return () => document.removeEventListener("contextmenu", handleContextMenu);
  }, []);

  return (
    <PublicLayout>
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative pt-10 pb-16 md:pt-16 md:pb-24">
        <Container className="grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
          <div className="text-center lg:text-right">
            <img
              src={logo}
              alt=""
              className="mx-auto mb-6 w-36 drop-shadow-2xl lg:hidden"
              draggable={false}
            />
            <Badge tone="gold" className="mb-5">
              لعبة الأسئلة العربية للعائلة
            </Badge>
            <h1 className="text-[2.1rem] leading-[1.25] sm:text-5xl lg:text-6xl font-extrabold">
              أفضل لعبة عربية
              <br />
              <span className="text-primary">للعائلة والأصدقاء</span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-white/75 leading-relaxed max-w-xl mx-auto lg:mx-0">
              أنشئ لعبتك من الموقع أو التطبيق، اعرضها على التلفزيون، وتنافسوا في
              فئات من كل مجال.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              {isAuthenticated ? (
                <>
                  <Button to="/app/games" size="lg" icon={<FaGamepad />}>
                    ألعابي
                  </Button>
                  <Button to="/app/games/create" size="lg" variant="secondary" icon={<FaPlus />}>
                    أنشئ لعبة
                  </Button>
                </>
              ) : (
                <>
                  <Button to="/register" size="lg" iconEnd={<FaArrowLeft className="text-base" />}>
                    ابدأ مجاناً
                  </Button>
                  <Button to="/pricing" size="lg" variant="secondary" icon={<FaCrown className="text-primary" />}>
                    الباقات
                  </Button>
                </>
              )}
            </div>
            {oldGame && (
              <div className="mt-4 flex justify-center lg:justify-start">
                <Button to="/old" variant="ghost" size="sm" icon={<FaHistory />}>
                  كمّل اللعبة السابقة
                </Button>
              </div>
            )}
          </div>

          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute inset-x-10 -top-6 bottom-24 hidden lg:block rounded-full bg-primary/20 blur-3xl"
            />
            <img
              src={logo}
              alt="تربع"
              className="relative mx-auto -mb-4 hidden w-64 xl:w-72 drop-shadow-2xl lg:block"
              draggable={false}
            />
            <Card tone="solid" pad="lg" className="relative shadow-2xl shadow-black/30">
              <h2 className="text-xl font-extrabold">عندك كود؟</h2>
              <p className="text-white/65 text-sm mt-1 mb-4">
                كود اللعبة يفتحها على الشاشة، وكود الخصم يوديك للاشتراك.
              </p>
              <CodeEntry showHelp={false} />
              <button
                type="button"
                onClick={() => setHelpOpen((v) => !v)}
                aria-expanded={helpOpen}
                className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-primary hover:text-mainText"
              >
                كيف أحصل على كود اللعبة؟
                <FaChevronDown className={`text-xs transition-transform ${helpOpen ? "rotate-180" : ""}`} />
              </button>
              {helpOpen && (
                <div className="mt-3">
                  <GameCodeHelp />
                </div>
              )}
            </Card>
          </div>
        </Container>
      </section>

      {/* ── How it works ─────────────────────────────────── */}
      <Section id="how" eyebrow="بثلاث خطوات" title="كيف تلعب؟">
        <ol className="grid gap-4 md:grid-cols-3">
          {STEPS.map(({ Icon, title, text }, i) => (
            <li key={title}>
              <Card className="h-full">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-11 h-11 rounded-xl bg-mainButton text-white flex items-center justify-center text-lg">
                    <Icon aria-hidden="true" />
                  </span>
                  <span className="text-white/40 font-extrabold text-2xl">{i + 1}</span>
                </div>
                <h3 className="text-lg font-bold">{title}</h3>
                <p className="text-white/70 mt-2 leading-relaxed">{text}</p>
              </Card>
            </li>
          ))}
        </ol>
      </Section>

      {/* ── Features ─────────────────────────────────────── */}
      <Section
        id="features"
        eyebrow="ليش تربع؟"
        title="كل اللي تحتاجه لسهرة ممتعة"
        lead="لعبة جماعية تجمع العائلة والأصدقاء، فيها تحدي ومعلومة وضحك."
        className="bg-white/[0.02]"
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ Icon, title, text }) => (
            <Card key={title} className="flex gap-4">
              <span className="w-11 h-11 shrink-0 rounded-xl bg-primary/15 text-primary flex items-center justify-center text-lg">
                <Icon aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-bold">{title}</h3>
                <p className="text-white/70 text-sm mt-1 leading-relaxed">{text}</p>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {/* ── Categories ───────────────────────────────────── */}
      {modes?.length > 0 && (
        <Section
          id="modes"
          eyebrow="الفئات"
          title="فئات تناسب كل الأعمار"
          lead="مجموعة من الفئات الشيقة، وكل فترة نضيف جديد."
        >
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {modes.map((mode) => (
              <li
                key={mode._id}
                className="overflow-hidden rounded-2xl border border-white/10 bg-background"
              >
                <div className="aspect-square">
                  <img
                    className="h-full w-full object-cover"
                    src={mode.image}
                    alt=""
                    loading="lazy"
                  />
                </div>
                <h3 className="px-2 py-2.5 text-center text-sm md:text-base font-bold leading-tight">
                  {mode.name}
                </h3>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* ── Premium ──────────────────────────────────────── */}
      <Section id="premium">
        <Card tone="accent" pad="none" className="overflow-hidden">
          <div className="grid lg:grid-cols-[1.3fr_1fr]">
            <div className="p-6 md:p-10">
              <Badge tone="copper" className="mb-4">
                <FaCrown aria-hidden="true" /> الاشتراك المميز
              </Badge>
              <h2 className="text-2xl md:text-4xl font-extrabold leading-tight">
                افتح كل شي بلا حدود
              </h2>
              <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                {PREMIUM_BENEFITS.map((b) => (
                  <li key={b.text} className="flex items-center gap-2 text-white/90">
                    <FaCheck className="shrink-0 text-success" aria-hidden="true" />
                    {b.text}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button to="/pricing" size="lg" iconEnd={<FaArrowLeft className="text-base" />}>
                  شوف الباقات
                </Button>
                {cheapest && (
                  <p className="text-white/70">
                    تبدأ من{" "}
                    <b className="text-white text-lg">
                      {formatMoney(cheapest.price, plansData.currency)}
                    </b>
                  </p>
                )}
              </div>
            </div>
            <div className="border-t lg:border-t-0 lg:border-r border-white/10 bg-header/40 p-6 md:p-10 flex flex-col justify-center">
              <span className="w-12 h-12 rounded-2xl bg-primary text-header flex items-center justify-center text-xl mb-4">
                <FaTag aria-hidden="true" />
              </span>
              <h3 className="text-xl font-extrabold">عندك كود خصم؟</h3>
              <p className="text-white/70 mt-2 leading-relaxed">
                افتح الرابط اللي وصلك، أو اكتب الكود في صفحة الاشتراك. نطبّقه على
                الباقات وتشوف السعر بعد الخصم قبل الدفع.
              </p>
              <Button to="/pricing" variant="gold" className="mt-5 self-start">
                استخدم كود الخصم
              </Button>
            </div>
          </div>
        </Card>
      </Section>

      {/* ── App download ─────────────────────────────────── */}
      <Section id="download">
        <Card className="flex flex-col items-center gap-6 text-center md:flex-row md:text-right md:justify-between">
          <div>
            <h2 className="text-2xl md:text-3xl font-extrabold">حمّل تطبيق تربع</h2>
            <p className="text-white/70 mt-2">
              أنشئ ألعابك والعب من جوالك وين ما كنت.
            </p>
          </div>
          <StoreBadges className="justify-center" />
        </Card>
      </Section>

      {/* ── FAQ ──────────────────────────────────────────── */}
      <Section id="faq" eyebrow="أسئلة شائعة" title="عندك سؤال؟" narrow>
        <div className="space-y-3">
          {FAQ.map(({ q, a }) => (
            <FaqItem key={q} q={q}>
              {a}
            </FaqItem>
          ))}
        </div>
      </Section>
    </PublicLayout>
  );
};

export default Home;
